import React, { useContext, useEffect, useMemo, useState } from 'react';
import MUIDataTable, { MUIDataTableColumn, MUIDataTableColumnOptions, MUIDataTableMeta, SelectableRows } from 'mui-datatables';
import { MUITranslations } from '../../helpers/MUITableTranslations';
import { Box, Button, FormControl, IconButton, InputLabel, ListItemText, MenuItem, Select, Tooltip } from '@material-ui/core';
import AddCircleTwoToneIcon from '@material-ui/icons/AddCircleTwoTone';
import moment from 'moment';
import { ApplicationContext } from '../../context/Contexts';
import { useAppSelector } from '../../store/configureStore';
import { getAppNameColor, getDay, getHour } from '../../utils/appointments';
import { useHistory } from 'react-router';
import { CreateNewFolder, Visibility } from '@material-ui/icons';
import { NewCaseAppointment } from './NewCaseAppointment';
import Folder from '@material-ui/icons/Folder';
import { AppointmentFilterStateKeys, AppointmentFiltersPayloadType, AppointmentTableColumns, AppointmentType, AppointmentsFiltersType } from '../../types/Agenda';
import * as R from 'ramda';
import { getAppointmentFiltersRequest } from './agenda.requests';
import { DropDownSelect } from './DropDownSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core';
import MuiTheme from '../../theme';
import { Organization } from '../../interfaces/Organization';
import { Subject, takeUntil } from 'rxjs';

const initialFilters: AppointmentFiltersPayloadType = {
  appointmentIds: [],
  services: [],
  formatedDateList: [],
  formatedHourList: [],
  makeList: [],
  modelList: [],
  plateNumberList: [],
  appointmentStatuses: [],
  formattedDateCreationList: [],
  formattedHourCreationList: [],
  appAppointmentIds: [],
  appointmentCategoryTypes: [],
}

type AppointmentTableProps = {
  appointments: AppointmentType[],
  getAppointment: (filter: AppointmentsFiltersType) => void,
  tableProps: any,
  setOpenModal: any
}

export const AppointmentsTable = ({ appointments, getAppointment, tableProps, setOpenModal }: AppointmentTableProps) => {
  const context = useContext(ApplicationContext);
  const history = useHistory();
  const translatorService = context.translatorService;
  const language = useAppSelector((state) => state.app?.language) || 'ro';
  const statusList = useAppSelector((state) => state.agenda?.statusList)
  const [filters, setFilters] = useState<AppointmentFiltersPayloadType>();
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);  
  const [selectedfilters, setSelectedFilters] = useState<AppointmentFiltersPayloadType>(initialFilters)
  const allValue = translatorService.Tranlate('APPOINTMENT_TABLE_ALL_VALUE', 'Toate');
  const [formatedHours, setFormatedHours] = useState<string[]>();
  const longDateFormat = useAppSelector((state) => state.app?.longDateFormat);
  const dateFormat = useAppSelector((state) => state.app?.dateFormat);
  const viewPartnerAppointments = useAppSelector((state) => state.app?.appUser?.organization?.viewOwnerEntries ?? false);
  const showAppointmentService = useAppSelector((state) => state.app?.appUser?.organization?.showAppointmentService ?? false);
  const displayServiceColumn = viewPartnerAppointments || showAppointmentService;
  const [tablePageNumber, setTablePageNumber] = useState<number>(1);
  const [tablePageSize, setTablePageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");  
  const refreshService = context.refreshService;
  
  const organizationId = useAppSelector((state) => state.app?.appUser?.organization?.id ?? null);

  const selectedfiltersRef = React.useRef<AppointmentFiltersPayloadType>(selectedfilters);
  useEffect(() => {
    selectedfiltersRef.current = selectedfilters;
  }, [selectedfilters]);

  const filtersRef = React.useRef<AppointmentFiltersPayloadType | undefined>(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const getAppointmentFilters = async () => {
    setIsLoading(false)
    const res = await getAppointmentFiltersRequest(viewPartnerAppointments ? organizationId : null);
    const filters = res.data as AppointmentFiltersPayloadType
    if (res.error || !filters) {
      setIsLoading(false)
      return;
    }
    const formattedHourCreationListToLocal = filters?.formattedHourCreationList.map((item) => {
      return getHour(moment
        ?.utc(item)
        ?.local()
        ?.format(longDateFormat || 'DD/MM/YYYY HH:mm'))
    })
    setFormatedHours(formattedHourCreationListToLocal);
    setFilters(filters)
    setIsLoading(false)
  }

  const getStatusIds = (filterList: any) => {
    const statusList: any = [];
    const statusNameMap: { [key: string]: string } = {};
    filterList.appointmentStatuses?.forEach((selectedStatus: string) => {
      statusNameMap[selectedStatus] = selectedStatus;
    });
    filtersRef.current?.appointmentStatuses?.forEach((status: any) => {
      const index = status.translations.findIndex((translation: any) => statusNameMap[translation.name]);
      if (index !== -1) {
        statusList.push(status.id);
      }
    });
    return statusList;
  };

  const getServiceIds = (filterList: any) => {
    const serviceList: any[] = [];
    const serviceNameMap: { [key: string]: string } = {};

    // Construim maparea cu serviciile selectate
    filterList.services?.forEach((selectedService: string) => {
      serviceNameMap[selectedService] = selectedService;
    });

    // Iterăm prin toate serviciile disponibile și verificăm dacă există în mapare
    filtersRef.current?.services?.forEach((service: Organization) => {
      if (serviceNameMap[service.name]) {
        serviceList.push(service.id);
      }
    });

    return serviceList;
  };


  const getAppointmentCategoryTypeIds = (filterList: any) => {
    const appointmentCategoryTypeList: any = [];
    const appointmentCategoryTypeNameMap: { [key: string]: string } = {};
    filterList.appointmentCategoryTypes?.forEach((selectedAppointmentCategoryType: string) => {
      appointmentCategoryTypeNameMap[selectedAppointmentCategoryType] = selectedAppointmentCategoryType;
    });

    filtersRef.current?.appointmentCategoryTypes?.forEach((status: any) => {
      const index = status.translations.findIndex((translation: any) => appointmentCategoryTypeNameMap[translation.name]);
      if (index !== -1) {
        appointmentCategoryTypeList.push(status.id);
      }
    });

    return appointmentCategoryTypeList;
  };

  const getFormatedHour = (selectedValue: string[]) => {
    const futureArray = selectedValue.map((hour) => {
      const index = formatedHours?.findIndex((val) => val === hour);
      return getHour(filtersRef.current?.formattedHourCreationList[index])
    })
    return futureArray;
  }

  const generatePayload = (filterList: AppointmentFiltersPayloadType) => {
    const payload = JSON.parse(JSON.stringify(filterList));
    const statusIds = payload.appointmentStatuses?.length > 0 ? getStatusIds(payload) : [];
    delete payload.appointmentStatuses

    const appointmentCategoryTypeIds = payload.appointmentCategoryTypes?.length > 0 ? getAppointmentCategoryTypeIds(filterList) : [];
    delete payload.appointmentCategoryTypes;

    const serviceIds = payload.services?.length > 0 ? getServiceIds(payload) : [];
    delete payload.services;
    return {
      ...payload,
      statusIds: statusIds,
      appointmentCategoryTypeIds: appointmentCategoryTypeIds,
      serviceIds: serviceIds,
    }
  }

  const applyFilters = (currentFilterList: any) => {
    const futureFilterList: any = { ...selectedfilters };
    Object.keys(selectedfilters).map((key: string) => {
      const selectedValue = currentFilterList[AppointmentFilterStateKeys[key]];
      if (key === 'formattedHourCreationList') {
        futureFilterList[key] = selectedValue[0] === allValue ? [...new Set(formatedHours)] : selectedValue?.length === 0 ? getFormatedHour(selectedValue) : getFormatedHour(selectedfilters[key])
      } else {
        futureFilterList[key] = selectedValue[0] === allValue ? filtersRef.current[key] : selectedValue?.length === 0 ? selectedfilters[key] : selectedValue
      }
    });
    setSelectedFilters(futureFilterList);
    const filterPayload = generatePayload(futureFilterList);
 
    getAppointment({
      ...filterPayload,
      includedAdditionalData: true,
      organizationOwnerId: viewPartnerAppointments ? organizationId : null,
      page: 1,
      pageSize: tableProps?.rowsPerPage,
    })
  };

  useEffect(() => {
    const destroy: Subject<void> = new Subject<void>();
    refreshService.RefreshAppointments$().pipe(takeUntil(destroy)).subscribe(() => {
      const currentFilters = selectedfiltersRef.current;

      console.log(currentFilters);

      const futureFilterList: any = { ...selectedfiltersRef.current };
      Object.keys(selectedfiltersRef.current).map((key: string) => {
      const selectedValue = currentFilters[key];
      if(!selectedValue) {
        return;
      }
      if (key === 'formattedHourCreationList') {
        futureFilterList[key] = selectedValue[0] === allValue ? [...new Set(formatedHours)] : selectedValue?.length === 0 ? getFormatedHour(selectedValue) : getFormatedHour(selectedfilters[key])
      } else {
        futureFilterList[key] = selectedValue[0] === allValue ? filtersRef.current[key] : selectedValue?.length === 0 ? currentFilters[key] : selectedValue
      }
    });
    setSelectedFilters(futureFilterList);
    const filterPayload = generatePayload(futureFilterList);
 
    getAppointment({
      ...filterPayload,
      includedAdditionalData: true,
      organizationOwnerId: viewPartnerAppointments ? organizationId : null,
      page: 1,
      pageSize: tableProps?.rowsPerPage,
    });
    });
    return () => {
      destroy.next();
    }
  }, []);

  useEffect(()=>{
    if(language){
      getAppointmentFilters();
    }
  }, [])

  const options = {
    filter: true,
    serverSide: true,
    selectableRows: 'none' as SelectableRows,
    selectableRowsOnClick: false,
    print: false,
    responsive: 'scroll',
    download: false,
    textLabels: MUITranslations.GetTranslations(translatorService),
    enableNestedDataAccess: '.',
    sort: false,
    count: tableProps?.total || 0,
    page: tableProps?.page || 0,
    rowsPerPage: tableProps?.rowsPerPage || 10,
    data: appointments,
    displayData: appointments,
    searchText: tableProps?.search || '',
    // responsive: "stacked",    

    onTableChange: (action: string, tableState: any) => {
      if (action === 'changeRowsPerPage' || action === "changePage") {
        const filterPayload = generatePayload(selectedfilters);

        getAppointment({
          ...filterPayload,
          page: tableState?.page + 1,
          pageSize: tableState?.rowsPerPage,
          text: tableState?.searchText,
          includedAdditionalData: true,
          organizationOwnerId: viewPartnerAppointments ? organizationId : null,
        })
      } else if (action === 'search') {
        const filterPayload = generatePayload(selectedfilters);

        getAppointment({
          ...filterPayload,
          page: 1,
          pageSize: tableState?.rowsPerPage,
          text: tableState?.searchText,
          includedAdditionalData: true,
          organizationOwnerId: viewPartnerAppointments ? organizationId : null,
        })
      }
      if (tableState && tableState.page) {
        setTablePageNumber(tableState.page);
      }
      if (tableState && tableState.rowsPerPage) {
        setTablePageSize(tableState.rowsPerPage);
      }
      if (tableState) {
        setSearchText(tableState.searchText);
      }
    },
    customToolbar: () => {
      return (<>
        <Tooltip title={translatorService.Tranlate('Add', 'Adauga')}>
          <IconButton
            aria-label="add"
            color="secondary"
            onClick={(e) => {
              history.push('/appointment')
            }}
          >
            <AddCircleTwoToneIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={translatorService.Tranlate('Calendar', 'Calendar')}>
          <IconButton
            aria-label="add"
            color="secondary"
            onClick={() => setOpenModal(true)}
          >
            <FontAwesomeIcon icon={['fas', 'calendar']} style={{ width: 20, height: 20, color: 'var(--primary)' }} />
          </IconButton>
        </Tooltip>

      </>

      );
    },
    onFilterChange: (column, filterList, type) => {
      if (type === 'reset') {
        setSelectedFilters(initialFilters)
      } else if (type === 'custom' && selectedfilters[AppointmentTableColumns[column as keyof typeof AppointmentTableColumns]]?.length !== 0 && column !== 'date' && column !== 'dateCreation') {
        const futureFilterList: AppointmentFiltersPayloadType = JSON.parse(JSON.stringify(selectedfilters));
        futureFilterList[AppointmentTableColumns[column as keyof typeof AppointmentTableColumns]] = [];
        setSelectedFilters(futureFilterList)
      }
    },

    customFilterDialogFooter: (currentFilterList: any, applyNewFilters: any) => {
      return (
        <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'center' }}>
          <Button
            className="m-2"
            variant="contained"
            color="secondary"
            size="medium"
            onClick={() => { applyFilters(currentFilterList) }}
          >

            {translatorService.Tranlate('APPOINTMENT_TABLE_APPLY_FILTERS', 'Apply Filters')}
          </Button>
        </div>
      );
    },
  };

  const COLUMNS: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: 'id',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_NO', 'No.'),
        options: {
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.appointmentIds]?.length === 0 && selectedfilters?.appointmentIds?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.appointmentIds] = selectedfilters.appointmentIds
              }
              const optionValues = filters?.appointmentIds ? [allValue, ...filters?.appointmentIds] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_NO', 'No.')}
              />
            },
          }
        }
      },
      {
        name: 'service.name',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_SERVICE_NAME', 'Nume Service'),
        options: {
          display: displayServiceColumn,
          filter: displayServiceColumn,
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.services]?.length === 0 && selectedfilters?.services?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.services] = selectedfilters.services
              }
              let optionValues: any = [allValue];
              if (filters?.services) {
                optionValues = filters?.services.map(obj => {
                  return obj ? obj.name : ''
                });
              }
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_SERVICE_NAME', 'Nume Service')}
              />
            },
          }
        }
      },
      {
        name: 'dateCreation',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_DATE_CREATION', 'Creation Date'),
        options: {
          customBodyRender: (value: string) => getDay(new Date(value), dateFormat || 'DD/MM/YYYY'),
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.formattedDateCreationList]?.length === 0 && selectedfilters?.formattedDateCreationList?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.formattedDateCreationList] = selectedfilters.formattedDateCreationList
              }

              const optionValues = filters?.formattedDateCreationList ? [allValue, ...filters?.formattedDateCreationList] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_DATE_CREATION', 'Creation Date')}
              />
            },
          }
        }
      },
      {
        name: 'dateCreation',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_HOUR_CREATION', 'Creation Hour'),
        options: {
          customBodyRender: (value: string) => getHour(moment
            .utc(value)
            .local()
            .format(longDateFormat || 'DD/MM/YYYY HH:mm')),
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.formattedHourCreationList]?.length === 0 && selectedfilters?.formattedHourCreationList?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.formattedHourCreationList] = selectedfilters.formattedHourCreationList
              }
              const uniqueArray = [...new Set(formatedHours)];
              const optionValues = uniqueArray ? [allValue, ...uniqueArray] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_HOUR_CREATION', 'Creation Hour')}
              />
            },
          }
        }
      },
      {
        name: "vehicle.model.brand",
        label: translatorService.Tranlate('VEHICLES_VEHICLE_MAKE', 'MARCA'),
        options: {
          sort: false,
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.makeList]?.length === 0 && selectedfilters?.makeList?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.makeList] = selectedfilters.makeList
              }
              const optionValues = filters?.makeList ? [allValue, ...filters?.makeList] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('VEHICLES_VEHICLE_MAKE', 'MARCA')}
              />
            },
          },
          customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
            const appointment = appointments.find((item) => item.id === tableMeta.rowData[0]);
            if (R.isNil(appointment)) {
              return;
            }

            return (
              <div>
                {appointment.vehicle?.identifiedMake || appointment.vehicle?.model?.brand || ''}
              </div>
            );
          }
        } as MUIDataTableColumnOptions
      },
      {
        name: 'vehicle.model.name',
        label: translatorService.Tranlate('FRM_CAR_DATA_MODEL', 'Model'),
        options: {
          sort: false,
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.modelList]?.length === 0 && selectedfilters?.modelList?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.modelList] = selectedfilters.modelList
              }
              const optionValues = filters?.modelList ? [allValue, ...filters?.modelList] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('FRM_CAR_DATA_MODEL', 'Model')}
              />
            },
          },
          customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
            const appointment = appointments.find((item) => item.id === tableMeta.rowData[0]);
            if (R.isNil(appointment)) {
              return;
            }

            return (
              <div>
                {appointment.vehicle?.identifiedModel || appointment.vehicle?.model?.name || ''}
              </div>
            );
          }
        } as MUIDataTableColumnOptions
      },
      {
        name: 'vehicle.plateNumber',
        label: translatorService.Tranlate('FRM_NOTICE_PLATE_NUMBER', 'Numar Inmatriculare'),
        options: {
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.plateNumberList]?.length === 0 && selectedfilters?.plateNumberList?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.plateNumberList] = selectedfilters.plateNumberList
              }
              const optionValues = filters?.plateNumberList ? [allValue, ...filters?.plateNumberList] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                name={translatorService.Tranlate('FRM_NOTICE_PLATE_NUMBER', 'Numar Inmatriculare')}
                optionValues={optionValues}
              />
            },
          }
        }
      },
      {
        name: 'date',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_DATE', 'Data'),
        options: {
          customBodyRender: (value: string) => value ? getDay(new Date(value), dateFormat || 'DD/MM/YYYY') : '',
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.formatedDateList]?.length === 0 && selectedfilters?.formatedDateList?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.formatedDateList] = selectedfilters.formatedDateList
              }
              const optionValues = filters?.formatedDateList ? [allValue, ...filters?.formatedDateList] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_DATE', 'Data')}
              />
            },
          }
        }
      },
      {
        name: 'date',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_TIME', 'Ora'),
        options: {
          customBodyRender: (value: string) => value ? getHour(new Date(value)) : '',
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.formatedHourList]?.length === 0 && selectedfilters?.formatedHourList?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.formatedHourList] = selectedfilters.formatedHourList
              }
              const optionValues = filters?.formatedHourList ? [allValue, ...filters?.formatedHourList] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_TIME', 'Ora')}
              />
            },
          }
        }
      },
      {
        name: 'appointmentStatus',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_STATUS', 'Status'),
        options: {
          customBodyRender: (value: any) => {
            const indexLang = value.translations.findIndex((trans: any) => trans.language === language);
            return indexLang !== -1 ? value.translations[indexLang].name : value.name;
          },
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.appointmentStatuses]?.length === 0 && selectedfilters?.appointmentStatuses?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.appointmentStatuses] = selectedfilters.appointmentStatuses
              }
              let optionValues: any = [allValue];
              if (filters?.appointmentStatuses) {
                optionValues = filters?.appointmentStatuses.map(obj => {
                  const status = obj.translations.find(translation => translation.language === language)
                  return status ? status.name : ''
                });
              }

              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_STATUS', 'Status')}
              />
            },
          }
        }
      },
      {
        name: 'appAppointmentId',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_ID', 'ID'),
        options: {
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.appAppointmentIds]?.length === 0 && selectedfilters?.appAppointmentIds?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.appAppointmentIds] = selectedfilters.appAppointmentIds
              }
              const optionValues = filters?.appAppointmentIds ? [allValue, ...filters?.appAppointmentIds] : [allValue];
              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_ID', 'ID')}
              />
            },
          }
        }
      },
      {
        name: 'appointmentCategoryType',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_CATEGORY_TYPE', 'Tip'),
        options: {
          customBodyRender: (value: any) => {
            const indexLang = value.translations.findIndex((trans: any) => trans.language === language);
            return indexLang !== -1 ? value.translations[indexLang].name : value.name;
          },
          filter: true,
          display: 'true',
          filterType: 'custom',
          filterOptions: {
            display: (filterList, onChange, index, column) => {
              const futureFilterList = JSON.parse(JSON.stringify(filterList));
              if (filterList[AppointmentFilterStateKeys.appointmentCategoryTypes]?.length === 0 && selectedfilters?.appointmentCategoryTypes?.length > 0) {
                futureFilterList[AppointmentFilterStateKeys.appointmentCategoryTypes] = selectedfilters.appointmentCategoryTypes
              }
              let optionValues: any = [allValue];
              if (filters?.appointmentCategoryTypes) {
                optionValues = filters?.appointmentCategoryTypes.map(obj => {
                  const appointmentCategoryType = obj.translations.find(translation => translation.language === language)
                  return appointmentCategoryType ? appointmentCategoryType.name : ''
                });
              }

              return <DropDownSelect
                filterList={futureFilterList}
                onChange={onChange}
                index={index}
                column={column}
                optionValues={optionValues}
                name={translatorService.Tranlate('APPOINTMENT_TABLE_CATEGORY_TYPE', 'Tip')}
              />
            },
          }
        }
      },
      {
        name: 'appName',
        label: translatorService.Tranlate('APPOINTMENT_TABLE_APP_NAME', 'Aplicație sursă'),
        options: {
          filter: false,
          sort: false,
          display: 'true',
          customBodyRender: (value: any) => (
            <span style={{ color: getAppNameColor(value) }}>{value}</span>
          )
        }
      },
      {
        name: 'actions',
        label: translatorService.Tranlate(
          'APPOINTMENT_TABLE_ACTIONS',
          'Acțiuni'
        ),
        options: {
          filter: false,
          customBodyRender: (value, tableMeta, updateValue) => {
            // If there is no value, return empty div

            return (
              <Box style={{ display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <IconButton
                    aria-label="add"
                    style={{ padding: '2px' }}
                    onClick={async () => {
                      history.push(`/appointment/${appointments![tableMeta.rowIndex].id}`)
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </Box>
                {(appointments![tableMeta.rowIndex].vehicleId && appointments![tableMeta.rowIndex].appointmentStatus?.code === statusList.CONFIRMED?.code) || appointments![tableMeta.rowIndex].appointmentStatus?.code === statusList.PAYMENT_CONFIRMATION?.code || appointments![tableMeta.rowIndex].caseId ?
                  <Box><IconButton
                    aria-label="add"
                    style={{ marginTop: -12, padding: '2px' }}
                    onClick={async () => {
                      if (appointments![tableMeta.rowIndex].caseId) {
                        history.push('/cases/' + appointments![tableMeta.rowIndex].caseId, 'Case xx');
                      }
                      else {
                        setAppointmentId(appointments![tableMeta.rowIndex].id);
                      }
                    }}
                  >
                    {appointments![tableMeta.rowIndex].caseId ? <Folder /> : (viewPartnerAppointments && appointments![tableMeta.rowIndex].serviceId != organizationId ? null : <CreateNewFolder style={{ color: '#f67346' }} />)}
                  </IconButton></Box> : null}
              </Box>
            );
          }
        }
      }
    ],
    [appointments, filters, selectedfilters]
  );

  const getMuiTheme = () => createMuiTheme(MuiTheme, {
    overrides: {
      MuiTableCell: {
        root: {
          paddingLeft: "5px",
          paddingRight: "5px",
          paddingTop: "5px",
          paddingBottom: "5px"
        },
      },
      MUIDataTableBodyCell: {
        root: {
          paddingLeft: "5px",
          paddingRight: "5px",
          paddingTop: "0px",
          paddingBottom: "0px",
          height: "50px !important"
        }
      }
    } as any
  });


  return (
    <Box
      key={JSON.stringify(appointments)}
      className="agenda-table"
      style={{
        flex: 1
      }}
    >
      <MuiThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          key={language}
          title={translatorService.Tranlate(
            'APPOINTMENT_TABLE_TITLE',
            'Programări'
          )}
          data={appointments || []}
          options={options}
          columns={COLUMNS}
        />
      </MuiThemeProvider>
      {appointmentId ? <NewCaseAppointment appAppointmentId={appointmentId} close={() => setAppointmentId(null)} /> : null}
    </Box>
  );
};
